import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { Target, Users, Calendar, Trophy, MapPin } from 'lucide-react';
import { nflTeams } from '../data/nflData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const WhatIsNFL = () => {
  return (
    <div className="min-h-screen" data-testid="what-is-nfl-page">
      <PageHeader 
        title="¿Qué es la NFL?"
        subtitle="Todo sobre la liga de fútbol americano más importante del mundo"
        backgroundImage="https://images.unsplash.com/photo-1663563624897-de8972d7ce93?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Intro Section */}
        <section>
          <SectionHeader 
            title="Introducción al Fútbol Americano"
            quarter="1Q"
            icon={Target}
          />
          
          <div className="space-y-6 text-slate-300 leading-relaxed">
            <p className="text-lg">
              El <strong className="text-white">fútbol americano</strong> es un deporte de contacto y estrategia donde dos equipos 
              de 11 jugadores intentan avanzar un balón ovalado hasta la zona de anotación rival. Es una mezcla única de 
              <strong className="text-emerald-400"> atletismo, táctica y trabajo en equipo</strong>.
            </p>
            
            <div className="tactical-card p-6 rounded-r-xl">
              <h3 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                La NFL en números
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { number: "32", label: "Equipos" },
                  { number: "2", label: "Conferencias" },
                  { number: "17", label: "Partidos/Temporada" },
                  { number: "1", label: "Super Bowl" }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <span 
                      className="text-4xl md:text-5xl font-black text-emerald-400 block"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {stat.number}
                    </span>
                    <span className="text-slate-400 text-sm">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <p>
              La <strong className="text-white">NFL (National Football League)</strong> es la liga profesional de fútbol americano 
              en Estados Unidos y la más importante del mundo. Cuenta con 32 equipos repartidos en dos conferencias: 
              la <strong className="text-orange-400">AFC (American Football Conference)</strong> y la 
              <strong className="text-emerald-400"> NFC (National Football Conference)</strong>.
            </p>
          </div>
        </section>

        {/* Teams Section */}
        <section>
          <SectionHeader 
            title="Los 32 Equipos"
            quarter="2Q"
            icon={Users}
          />
          
          <Tabs defaultValue="AFC" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800 rounded-xl p-1 mb-8">
              <TabsTrigger 
                value="AFC" 
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                AFC
              </TabsTrigger>
              <TabsTrigger 
                value="NFC"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                NFC
              </TabsTrigger>
            </TabsList>
            
            {Object.entries(nflTeams).map(([conference, divisions]) => (
              <TabsContent key={conference} value={conference} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(divisions).map(([division, teams]) => (
                    <div key={division} className="info-card">
                      <h4 
                        className="text-lg font-bold text-white mb-4 flex items-center gap-2"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        <MapPin className="w-4 h-4 text-emerald-400" />
                        {conference} {division}
                      </h4>
                      <ul className="space-y-2">
                        {teams.map((team, index) => (
                          <li key={index} className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-slate-600" />
                            {team}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </section>

        {/* Season Structure */}
        <section>
          <SectionHeader 
            title="Estructura de la Temporada"
            quarter="3Q"
            icon={Calendar}
          />
          
          <div className="space-y-6">
            {[
              {
                title: "Pretemporada",
                period: "Agosto",
                description: "3 partidos de exhibición donde los equipos prueban jugadores y nuevas estrategias. Los resultados no cuentan.",
                color: "#64748b"
              },
              {
                title: "Temporada Regular",
                period: "Septiembre - Enero",
                description: "17 partidos por equipo durante 18 semanas. Los mejores equipos clasifican a playoffs.",
                color: "#22c55e"
              },
              {
                title: "Playoffs",
                period: "Enero - Febrero",
                description: "14 equipos compiten en rondas eliminatorias. Wild Card, Divisional, Championship y Super Bowl.",
                color: "#f97316"
              },
              {
                title: "Super Bowl",
                period: "Primer domingo de febrero",
                description: "La gran final. El campeón de la AFC vs el campeón de la NFC por el trofeo Lombardi.",
                color: "#eab308"
              }
            ].map((phase, index) => (
              <div 
                key={index}
                className="flex gap-6 group"
              >
                <div className="flex flex-col items-center">
                  <div 
                    className="w-4 h-4 rounded-full border-2 flex-shrink-0 transition-transform duration-300 group-hover:scale-125"
                    style={{ borderColor: phase.color, backgroundColor: `${phase.color}30` }}
                  />
                  {index < 3 && (
                    <div className="w-0.5 h-full bg-slate-700 mt-2" />
                  )}
                </div>
                <div className="pb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 
                      className="text-xl font-bold text-white uppercase"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {phase.title}
                    </h3>
                    <span 
                      className="px-3 py-1 text-xs font-bold rounded-full"
                      style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
                    >
                      {phase.period}
                    </span>
                  </div>
                  <p className="text-slate-400">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Objective */}
        <section>
          <SectionHeader 
            title="El Objetivo del Juego"
            quarter="4Q"
            icon={Trophy}
          />
          
          <div className="space-y-6 text-slate-300 leading-relaxed">
            <div className="tactical-card p-8 rounded-r-xl">
              <h3 
                className="text-2xl font-bold text-white mb-4 uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Objetivo Principal
              </h3>
              <p className="text-lg">
                <strong className="text-emerald-400">Anotar más puntos que el rival</strong> avanzando por el campo hasta la 
                zona de anotación (touchdown) o pateando a través de los postes (field goal).
              </p>
            </div>
            
            <p>
              El campo se divide en <strong className="text-white">yardas</strong> (100 yardas entre zonas de anotación). 
              El equipo ofensivo tiene <strong className="text-orange-400">4 oportunidades (downs)</strong> para avanzar 
              al menos <strong className="text-orange-400">10 yardas</strong>. Si lo logra, renueva sus 4 oportunidades.
            </p>
            
            <p>
              Si no consigue las 10 yardas en 4 intentos, el balón pasa al equipo rival en ese punto del campo. 
              Por eso, muchos equipos eligen <strong className="text-white">patear (punt)</strong> en el cuarto down 
              para alejar el balón de su zona de anotación.
            </p>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h4 
                className="text-lg font-bold text-white mb-3"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Ejemplo práctico
              </h4>
              <p className="text-sm text-slate-400">
                Tu equipo tiene el balón en la yarda 20 de tu campo. Necesitas avanzar 80 yardas para llegar a la 
                end zone rival. En tu primera jugada, avanzas 6 yardas. Ahora es "2do y 4" (segundo down, faltan 4 yardas 
                para el primer down). Completas un pase de 5 yardas: ¡primer down! Ahora tienes 4 nuevas oportunidades.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WhatIsNFL;
